import { geoMercator, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import { zoom, zoomIdentity, type ZoomBehavior } from 'd3-zoom';
import { memo } from 'preact/compat';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { carregarGeo } from '../hooks';
import type { Camada, MapaResposta } from '../types';
import { SEM_DADO, textoTooltip, type Escala } from './escalas';

const W = 800;
const H = 600;

type Forma = { cod: string; nome: string; d: string };
type Dica = { cod: string; x: number; y: number };

type Props = {
  camada: Camada;
  dados: MapaResposta | null;
  escala: Escala;
  atualizando: boolean;
  selecionado: string | null;
  onSelecionar: (cod: string) => void;
  /** Texto do tooltip de um município, quando a camada não é uma das de ação/pesquisa (ex.: indicadores oficiais). */
  textoDica?: (cod: string) => string[];
};

/** Os 223 polígonos. Memoizado: passar o mouse por cima não redesenha o mapa inteiro. */
const Municipios = memo(function Municipios({ formas, cores }: { formas: Forma[]; cores: Map<string, string> }) {
  return (
    <g>
      {formas.map((f) => (
        <path
          key={f.cod}
          d={f.d}
          class="muni"
          data-cod={f.cod}
          fill={cores.get(f.cod) ?? SEM_DADO}
          vector-effect="non-scaling-stroke"
        />
      ))}
    </g>
  );
});

export function MapaPB({ camada, dados, escala, atualizando, selecionado, onSelecionar, textoDica }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const gRef = useRef<SVGGElement>(null);
  const caixaRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  const [formas, setFormas] = useState<Forma[] | null>(null);
  const [erroGeo, setErroGeo] = useState(false);
  const [dica, setDica] = useState<Dica | null>(null);

  useEffect(() => {
    let vivo = true;
    carregarGeo()
      .then((geo) => {
        if (!vivo) return;
        const proj = geoMercator().fitExtent(
          [
            [12, 12],
            [W - 12, H - 12],
          ],
          geo,
        );
        const caminho = geoPath(proj);
        setFormas(geo.features.map((f) => ({ cod: f.properties.codIbge, nome: f.properties.nome, d: caminho(f) ?? '' })));
      })
      .catch(() => vivo && setErroGeo(true));
    return () => {
      vivo = false;
    };
  }, []);

  // Zoom e arrasto: a transformação vai direto no <g>, sem passar pelo estado (fluido mesmo com 223 formas).
  useEffect(() => {
    const svg = svgRef.current;
    const g = gRef.current;
    if (!svg || !g || !formas) return;
    const z = zoom<SVGSVGElement, unknown>()
      .scaleExtent([1, 14])
      .extent([[0, 0], [W, H]])
      .translateExtent([[0, 0], [W, H]])
      .on('zoom', (e) => {
        g.setAttribute('transform', e.transform.toString());
        setDica(null);
      });
    zoomRef.current = z;
    select(svg).call(z).on('dblclick.zoom', null);
    return () => {
      select(svg).on('.zoom', null);
    };
  }, [formas]);

  const cores = useMemo(() => {
    const m = new Map<string, string>();
    for (const [cod, e] of Object.entries(dados?.municipios ?? {})) m.set(cod, escala.cor(e.valor));
    return m;
  }, [dados, escala]);

  const porCod = useMemo(() => new Map((formas ?? []).map((f) => [f.cod, f])), [formas]);

  function codDoAlvo(alvo: EventTarget | null): string | null {
    return alvo instanceof Element ? alvo.getAttribute('data-cod') : null;
  }

  function aoMover(e: PointerEvent) {
    const cod = codDoAlvo(e.target);
    const caixa = caixaRef.current?.getBoundingClientRect();
    if (!cod || !caixa) return setDica(null);
    setDica({ cod, x: e.clientX - caixa.left, y: e.clientY - caixa.top });
  }

  function aplicaZoom(fator: number | 'reset') {
    const svg = svgRef.current;
    const z = zoomRef.current;
    if (!svg || !z) return;
    if (fator === 'reset') select(svg).call(z.transform, zoomIdentity);
    else select(svg).call(z.scaleBy, fator);
  }

  const alvo = dica ? porCod.get(dica.cod) : undefined;
  const linhas = dica ? (textoDica ? textoDica(dica.cod) : textoTooltip(camada, dados?.municipios[dica.cod], dados?.amostraMinima)) : [];
  const larguraCaixa = caixaRef.current?.clientWidth ?? W;
  const viraEsquerda = dica ? dica.x > larguraCaixa - 230 : false;

  return (
    <div class="mapa-caixa" ref={caixaRef}>
      {erroGeo && <p class="mapa-erro">Não foi possível carregar o mapa.</p>}
      {!formas && !erroGeo && <p class="mapa-carregando">Carregando mapa…</p>}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Mapa dos 223 municípios da Paraíba"
        onPointerMove={aoMover}
        onPointerLeave={() => setDica(null)}
        onClick={(e) => {
          const cod = codDoAlvo(e.target);
          if (cod) onSelecionar(cod);
        }}
      >
        <defs>
          {/* Padrão hachurado: município sem dado (distinto de "zero"). */}
          <pattern id="semdado" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" class="semdado-a" />
            <line x1="0" y1="0" x2="0" y2="6" class="semdado-b" stroke-width="2" />
          </pattern>
        </defs>
        <g ref={gRef}>
          {formas && <Municipios formas={formas} cores={cores} />}
          {/* Destaques por cima de tudo, sem capturar o mouse. */}
          {selecionado && porCod.get(selecionado) && (
            <path d={porCod.get(selecionado)!.d} class="muni-selecionado" vector-effect="non-scaling-stroke" />
          )}
          {alvo && dica && dica.cod !== selecionado && <path d={alvo.d} class="muni-hover" vector-effect="non-scaling-stroke" />}
        </g>
      </svg>

      <div class="mapa-zoom" role="group" aria-label="Controles de zoom">
        <button type="button" onClick={() => aplicaZoom(1.6)} aria-label="Aproximar">+</button>
        <button type="button" onClick={() => aplicaZoom(1 / 1.6)} aria-label="Afastar">−</button>
        <button type="button" onClick={() => aplicaZoom('reset')} aria-label="Ver o estado inteiro">⤢</button>
      </div>

      {atualizando && <span class="mapa-atualizando">Atualizando…</span>}

      {dica && alvo && (
        <div class="tooltip" style={{ left: viraEsquerda ? undefined : dica.x + 14, right: viraEsquerda ? larguraCaixa - dica.x + 14 : undefined, top: dica.y + 14 }} role="tooltip">
          <strong>{alvo.nome}</strong>
          {linhas.map((l) => (
            <span key={l}>{l}</span>
          ))}
        </div>
      )}
    </div>
  );
}
